//#region IMPORTS
import {
  InstoreOrder,
  InstoreOrderLineRequest,
  InstoreOrderLineTableRow,
  InstoreOrderLineCustomField,
} from "../../models/instoreOrder";
import {
  ADD_PERSO_VALUES_MODAL,
  CREATE_ORDERLINE_ROW_MODAL,
  INSTORE_ORDER_LINE_DELETE_SURE,
} from "../../models/_consts";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useExportInstoreOrderPersoItemsExcel } from "../../hooks/useExportInstoreOrderPersoItemsExcel";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import {
  instoreOrderLinesSelectors,
  setInstoreOrderLoading,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { useLocalInstoreOrderLines } from "../../hooks/useLocalInstoreOrderLines";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { useTranslation } from "react-i18next";
import { InstoreTheme } from "../../models/instoreTheme";
import { StoreFormat } from "../../models/storeFormat";
import { CellContext } from "@tanstack/react-table";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreOrderDetailTablePersoFieldArrayForm from "./InstoreOrderDetailTablePersoFieldArrayForm";
import InstoreOrderDetailTable from "./InstoreOrderDetailTable";
import InstoreOrderLineForm from "./InstoreOrderLineForm";
import InstoreOrderSummary from "./InstoreOrderSummary";
import InstoreOrderForm from "./InstoreOrderForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import ActionButton from "../genericButtons/actionButton/ActionButton";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../genericPageHeader/PageHeader";
import AreYouSure from "../modalTypes/AreYouSure";
import MainModal from "../_layout/MainModal";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  instoreOrder?: InstoreOrder;
  readOnly?: boolean;
  create?: boolean;
}
//#endregion

export default function InstoreOrderDetailContainer({
  instoreOrder,
  readOnly = false,
  create = false,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const { loading: loadingTemplates } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates,
  );
  const { loading } = useAppSelector(({ instoreOrders }) => instoreOrders);
  const { user } = useAppSelector(({ account }) => account);

  const instoreOrderLines = useAppSelector(
    instoreOrderLinesSelectors.selectAll,
  );

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  // Local state
  const [orderLineTableRowToDelete, setOrderLineTableRowToDelete] =
    useState<InstoreOrderLineTableRow>();
  const [storeFormatToUse, setStoreFormatToUse] = useState<StoreFormat>();
  const [templateToUse, setTemplateToUse] = useState<InstoreTemplate>();
  const [selectedCellContext, setSelectedCellContext] =
    useState<CellContext<InstoreOrderLineTableRow, any>>();

  useEffect(() => {
    if (instoreOrder) {
      setStoreFormatToUse({
        id: instoreOrder.store_format_id,
        name: instoreOrder.store_format,
        customer_id: 11,
        customer_name: "Rituals",
      });
    }
  }, [instoreOrder]);

  // Hooks
  const {
    moveRowAnywhere,
    localTableData,
    updateRowCells,
    addArticleRow,
    addThemeRow,
    removeRow,
    isDirty,
  } = useLocalInstoreOrderLines(storeFormatToUse);

  const { exportInstoreOrderPersoItems } =
    useExportInstoreOrderPersoItemsExcel();
  //#endregion

  //#region ADD THEME ROW
  const addThemeRowModalClickHandler = () => {
    dispatch(openModal(CREATE_ORDERLINE_ROW_MODAL));
  };

  const addThemeRowHandler = async (theme: InstoreTheme) => {
    addThemeRow(theme);
  };
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRowHandler = (themeId: number) => {
    addArticleRow(themeId);
  };
  //#endregion

  //#region MOVE ROW
  const moveRowHandler = (toIndex: number, row: InstoreOrderLineTableRow) => {
    if (row && row.flatIndex !== undefined) {
      moveRowAnywhere(row.flatIndex, toIndex);
    } else {
      toast.error("Cannot move row: missing index");
    }
  };

  //#region UPDATE ROW CELLS
  const updateRowCellsHandler = (
    columnId: string,
    value: any,
    row: InstoreOrderLineTableRow,
  ) => {
    updateRowCells(columnId, value, row);
  };
  //#endregion

  //#region REMOVE ROW
  const removeRowHandler = (row: InstoreOrderLineTableRow) => {
    setOrderLineTableRowToDelete(row);
    dispatch(openModal(INSTORE_ORDER_LINE_DELETE_SURE));
  };

  const removeRowConfirmHandler = async () => {
    if (!orderLineTableRowToDelete) return;

    // UX only
    removeRow(orderLineTableRowToDelete);

    // Backend
    const deletePromise = (async () => {
      dispatch(setInstoreOrderLoading(true));
      if (instoreOrder && orderLineTableRowToDelete) {
        const deleteIds = orderLineTableRowToDelete.db_ids;
        console.log(deleteIds);
        deleteIds &&
          (await agent.InstoreOrderLines.deleteMany(instoreOrder.id, {
            instore_order_line_ids: deleteIds,
          }));
      }
      cancelCRUD();
    })();

    toast.promise(deletePromise, {
      pending: "Deleting order line(s)… This may take a moment.",
      success: "Order line(s) deleted successfully.",
      error: "Failed to delete order line(s). Please try again.",
    });

    dispatch(setInstoreOrderLoading(false));
  };
  //#endregion

  //#region FLATTEN FOR BACKEND
  const flattenForBackend = useCallback(
    (rows: InstoreOrderLineTableRow[]): InstoreOrderLineRequest[] => {
      const out: InstoreOrderLineRequest[] = [];

      rows.forEach((parent) => {
        parent.children?.forEach((child) => {
          out.push({
            id: child.db_ids?.[0],
            theme_id: parent.theme_id,
            theme_name: parent.theme_name,
            store_format_name: child.store_format_name,
            store_format_id: child.store_format_id,
            custom_fields: child.custom_fields,
            article_name: child.article_name,
            amount: child.amount,
            index: child.flatIndex,
          });
        });
      });

      return out;
    },
    [],
  );
  //#endregion

  //#region EXPORT PERSO ITEMS
  const exportInstoreOrderPersoItemsHandler = (merged: boolean = false) => {
    if (!instoreOrder) return;
    exportInstoreOrderPersoItems(instoreOrder.id, merged);
  };

  const persoItemsAvailable = instoreOrderLines.some((ol) => ol.custom_fields);
  //#endregion

  //#region CLEANUP UI
  const cancelCRUD = () => {
    setOrderLineTableRowToDelete(undefined);
    setSelectedCellContext(undefined);
    dispatch(setInstoreOrderLoading(false));
    dispatch(openModal(false));
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={CREATE_ORDERLINE_ROW_MODAL} closeHandler={cancelCRUD}>
        <InstoreOrderLineForm
          addThemeRowHandler={addThemeRowHandler}
          usedThemes={Array.from(
            new Set(
              (instoreOrderLines ?? [])
                .map((r: any) => (r.theme ?? "").toLowerCase())
                .filter(Boolean),
            ),
          )}
          cancel={cancelCRUD}
        />
      </MainModal>

      <MainModal type={ADD_PERSO_VALUES_MODAL} closeHandler={cancelCRUD}>
        <InstoreOrderDetailTablePersoFieldArrayForm
          onSubmitHandler={(
            columnId: string,
            value: InstoreOrderLineCustomField[],
            row: InstoreOrderLineTableRow,
          ) => {
            updateRowCellsHandler(columnId, value, row);
            cancelCRUD();
          }}
          cellContext={selectedCellContext!}
          readOnly={readOnly}
          cancel={cancelCRUD}
        />
      </MainModal>

      <MainModal
        type={INSTORE_ORDER_LINE_DELETE_SURE}
        closeHandler={cancelCRUD}
        closeDisabled={loading}
      >
        <AreYouSure
          confirmHandler={removeRowConfirmHandler}
          closeHandler={cancelCRUD}
          disabled={loading}
          message="Are you sure you want to delete the selected order line? This action cannot be undone."
          title="Delete order line"
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          extraButtons={
            isAdmin ? (
              <div className="flex items-center gap-2">
                <ActionButton
                  clickHandler={() =>
                    exportInstoreOrderPersoItemsHandler(false)
                  }
                  hidePlusIcon
                  className="bg-lime-700 hover:bg-lime-500 focus-visible:outline-lime-500"
                  disabled={loading}
                  value={t("rituals.exportPersoItems")}
                  hide={!persoItemsAvailable}
                />
                <ActionButton
                  clickHandler={() => exportInstoreOrderPersoItemsHandler(true)}
                  hidePlusIcon
                  className="bg-lime-900 hover:bg-lime-600 focus-visible:outline-lime-600"
                  disabled={loading}
                  value={t("rituals.exportPersoItemsMerged")}
                  hide={!persoItemsAvailable}
                />
              </div>
            ) : null
          }
          disabled={loadingTemplates || loading || readOnly}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          title={
            create
              ? "New order"
              : instoreOrder && !readOnly
                ? `Edit ${instoreOrder.order_code}`
                : instoreOrder && readOnly
                  ? `${instoreOrder.order_code}`
                  : "Order detail"
          }
        >
          <GoBackButton />
        </PageHeader>

        {readOnly && instoreOrder ? (
          <InstoreOrderSummary
            loadingSummary={loading}
            instoreOrder={instoreOrder}
            orderLineItemsList={
              storeFormatToUse &&
              localTableData &&
              localTableData.length > 0 ? (
                <InstoreOrderDetailTable
                  setSelectedCellContext={setSelectedCellContext}
                  storeFormatToUse={storeFormatToUse}
                  readOnly
                  data={localTableData}
                />
              ) : null
            }
          />
        ) : (
          <InstoreOrderForm
            instoreOrderLinesFromTemplate={flattenForBackend(localTableData!)}
            setInstoreTemplateHandler={(template?: InstoreTemplate) => {
              setTemplateToUse(template);
            }}
            instoreOrderLineRequests={flattenForBackend(localTableData!)}
            setStoreFormatHandler={(format: StoreFormat) =>
              setStoreFormatToUse(format)
            }
            templateToUse={templateToUse}
            instoreOrder={instoreOrder}
            disabled={loadingTemplates || loading}
            isDirty={isDirty}
            orderLineItemsList={
              storeFormatToUse &&
              localTableData &&
              localTableData.length > 0 ? (
                <InstoreOrderDetailTable
                  updateRowCellsHandler={updateRowCellsHandler}
                  addParentRowHandler={addThemeRowModalClickHandler}
                  addChildRowHandler={addArticleRowHandler}
                  deleteRowHandler={removeRowHandler}
                  setSelectedCellContext={setSelectedCellContext}
                  storeFormatToUse={storeFormatToUse}
                  disabled={loadingTemplates || loading}
                  moveRow={moveRowHandler}
                  data={localTableData}
                />
              ) : null
            }
          />
        )}
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
